// NewsTalk (c) Copyright 2023 by Qwixotic
// 
// File:   Components.js
// Author: Percipient Reverence
// Date:   30-Apr-2023
//
// Description:
//
//   Generally useful components, also configuration values

// React imports
import { useEffect, useMemo, useState } from 'react'

// MUI imports
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import { gridPageCountSelector, gridPageSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArticleIcon from '@mui/icons-material/Article';
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import { Link as MuiLink } from '@mui/material'
import Pagination from '@mui/material/Pagination'
import Paper from '@mui/material/Paper'
import PersonIcon from '@mui/icons-material/Person';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TextField from '@mui/material/TextField';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { styled } from '@mui/material/styles';

// TinyMCE imports
import { Editor } from '@tinymce/tinymce-react';

// Local imports
import { ConfigVars, deleteSigninCookies, emailIsLegal, FormatDate, getCookie, gotoComment, myPost,
         nameIsLegal, nameOrEmailIsLegal, nodeJsURL, padZeros, passwordIsLegal, setSigninCookies } from './Util'
import '../NewsTalk.css'

export const Link = styled(MuiLink)`
      text-decoration: none;
      color: #2067DF;
      &:hover {
        text-decoration: underline;
        cursor: pointer;
      }`

// When "Sign Out" is clicked we have to check if we're on the AccountSettings page.  If we are we
// have to go to a non-account specific page (we'll go to '/') because the AccountSettings page
// won't work when no one's signed in.
//
// It's possible that I should always be going to a non-account specific page because there might be
// too many account specific pages.  There's editing a message, and if it's just that one and maybe
// a few more then it's reasonable to check for them, but not if there's, say, more than five and
// possibly counting.
const SignedinBox = (props) => {
  return (
    <Stack direction='column' alignItems='center' spacing={3}
           sx={{backgroundColor: '#eee', padding: '.5rem', borderStyle: 'solid', borderRadius: '8px',
                fontFamily: 'Roboto', width: '30rem'}}>
      <Stack direction='row' justifyContent='space-between' sx={{width: '80%'}}>
        <Button onClick={() => window.location = '/AccountSettings'}>
          Account Settings
        </Button>
        <Button onClick={() => {
                  setSigninCookies(0, '')
                  const endPoint = window.location.pathname.split('/').slice(-1)
                  if (endPoint && endPoint === 'AccountSettings') {
                    window.location = '/'
                  } else {
                    props.nameSetter('')
                    props.credentialSetter({idnum: 0})
                    props.popoverAnchorElSetter(null)
                    setTimeout(() => props.stateSetter('signinattempt'), 200)
                  }
                }}>
          Sign Out
        </Button>
      </Stack>
      <Box justifyContent='center'>
        <Button variant='contained' onClick={() => props.popoverAnchorElSetter(null)}>
          Close
        </Button>
      </Box>
    </Stack>
  )
}

const SigninAttemptBox = (props) => {
  const [showPassword, setShowPassword] = useState(false)
  const [passwordError, setPasswordError] = useState(false)
  const [passwordHelperText, setPasswordHelperText] = useState(null)
  const [nameOrEmail, setNameOrEmailError] = useState(false)
  const [nameOrEmailHelperText, setNameOrEmailHelperText] = useState(null)
  const [signinData, setSigninData] = useState(null)
  const handleClickShowPassword = () => setShowPassword((show) => !show)
  const handleMouseDownPassword = (event) => {event.preventDefault()}

  const onChangeCheckNameOrEmailField = (target) => {
    if (nameOrEmail) {
      if (nameOrEmailIsLegal(target.value)) {
        setNameOrEmailError(false)
        setNameOrEmailHelperText(null)
      }
    }
  }

  const onChangeCheckPasswordField = (target) => {
    if (passwordError) {
      if (passwordIsLegal(target.value)) {
        setPasswordError(false)
        setPasswordHelperText(null)
      }
    }
  }

  const submitSignin = (event) => {
    event.preventDefault()
    const Data = new FormData(event.currentTarget)
    const nameOrEmail = Data.get('nameoremail')
    const password = Data.get('password')
    let nameOrEmailLegal = true
    let passwordLegal = true

    if (nameOrEmailIsLegal(nameOrEmail)) {
      // Legal name or email address
      setNameOrEmailError(false)
    } else {
      // Illegal name or email address, indicate error
      nameOrEmailLegal = false
      setNameOrEmailError(true)
      setNameOrEmailHelperText('Not a valid name or email address')
    }
    if (passwordIsLegal(password)) {
      // Legal password
      setPasswordError(false)
    } else {
      // Illegal password, indicate error
      passwordLegal = false
      setPasswordError(true)
      setPasswordHelperText('Password must be at least 6 characters')
    }
      
    if (!nameOrEmailLegal || !passwordLegal) {
      return
    }

    // If we've gotten this far then we have a legal name or email address, and a legal password, but we still have
    // to validate these for signing in.  By setting signinData we'll initiate a POST to the backend
    // which will let us know whether it is valid or not.
    setSigninData({'nameoremail': nameOrEmail, 'qx': password})
  }

  // myPost() (a layer atop fetch()) has to be employed inside useEffect so that the helper text
  // updates aren't a cycle late
  useEffect(() => {
    if (signinData) {
      const data = signinData
      setSigninData(null)
      myPost('/nt/checkSigninAttempt', data)
        .then(response => {
          if (!response.ok) {
            throw Error(response.statusText)
          }
          return response.json()
        })
        .then(signinResult => {
          if (signinResult.status === 'Error') {
            if (signinResult.errorType === 'email' || signinResult.errorType === 'name' ) {
              setNameOrEmailError(true)
              setNameOrEmailHelperText(signinResult.message)
            } else {
              // 'password' is the only other error type, so no need to check
              setPasswordError(true)
              setPasswordHelperText(signinResult.message)
            }
          } else if (signinResult.status === 'Success') {
            props.credentialSetter(signinResult.data)
            setSigninCookies(signinResult.data.idnum, signinResult.data.pword)
            props.popoverAnchorElSetter(null)
            setTimeout(() => props.stateSetter('signedin'), 200)
          }
        })
        .catch(error => console.log('*** ERROR *** Signin problem: ' + error.message))
    }
  }, [props, signinData])

  return (
    <Stack direction='column' justifyContent='flex-start' component='form' onSubmit={(event) => submitSignin(event)}
           sx={{backgroundColor: '#eee', padding: '.5rem', borderStyle: 'solid', borderRadius: '8px',
                fontFamily: 'Roboto', width: '30rem'}}>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <div>
          <b>Sign In:</b>
        </div>
        <Button onClick={() => props.stateSetter('regname')}>
          Register
        </Button>
      </Stack>
      <TextField id='nameoremail' name='nameoremail' label='Name or Email Address' variant='outlined' fullwidth='true' autoFocus={true}
                 error={nameOrEmail}
                 helperText={nameOrEmailHelperText}
                 onChange={(event) => onChangeCheckNameOrEmailField(event.currentTarget, nameOrEmail)}
                 sx={{margin: '.5rem',
                      '& .MuiOutlinedInput-root': {borderRadius: '8px', backgroundColor: 'white',
                                                   '& fieldset': {borderWidth: '2px', borderColor: 'rgba(0, 0, 0, .07)'}}}}/>
      <TextField id='password' name='password' label='Password' variant='outlined' fullwidth='true' type={showPassword ? 'text' : 'password'}
                 InputProps={{endAdornment: <InputAdornment position='end'>
                                              <IconButton
                                                aria-label='toggle password visibility'
                                                onClick={handleClickShowPassword}
                                                onMouseDown={handleMouseDownPassword}
                                                edge='end'
                                              >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                              </IconButton>
                                            </InputAdornment>}}
                 error={passwordError}
                 helperText={passwordHelperText}
                 onChange={(event) => onChangeCheckPasswordField(event.currentTarget)}
                 sx={{margin: '.5rem',
                      '& .MuiOutlinedInput-root': {borderRadius: '8px', backgroundColor: 'white',
                                                   '& fieldset': {borderWidth: '2px', borderColor: 'rgba(0, 0, 0, .07)'}}}}/>
      <Box sx={{textAlign: 'center'}}>
        <Button variant='contained' type='submit'>
          Submit
        </Button>
      </Box>
      <Box>
        <Button size='small' onClick={() => console.log("forgot password")}>
          Forgot Password
        </Button>
      </Box>
    </Stack>
  )
}

const RegisterNameBox = (props) => {
  const [nameError, setNameError] = useState(false)
  const [nameHelperText, setNameHelperText] = useState(null)
  const [submittedName, setSubmittedName] = useState('')

  const onChangeCheckNameField = (target) => {
    if (nameError) {
      if (nameIsLegal(target.value)) {
        setNameError(false)
        setNameHelperText(null)
      }
    }
  }

  const submitName = (event) => {
    event.preventDefault()
    const Data = new FormData(event.currentTarget)
    const name = Data.get('name')

    if (nameIsLegal(name)) {
      // Legal name
      setNameError(false)
      setSubmittedName(name)
    } else {
      // Illegal name, indicate error
      setNameError(true)
      setNameHelperText('Names may contain letters, numbers and the characters "!?/$&^+-~.:;\'"[]{}" ' +
                        'and cannot be longer than 20 characters ')
      return
    }
  }

  // myPost() (a layer atop fetch()) has to be employed inside useEffect so that the helper text
  // updates aren't a cycle late
  useEffect(() => {  
    if (submittedName) {
      const name = submittedName
      setSubmittedName('')
      myPost('/nt/regname', {'name': name})
        .then(response => {
          if (!response.ok) {
            throw Error(response.statusText)
          }
          return response.json()
        })
        .then(regNameResult => {
          if (regNameResult.status === 'Error') {
            setNameError(true)
            setNameHelperText(regNameResult.message)
          } else if (regNameResult.status === 'Success') {
            props.stateSetter('regemail')
            props.nameSetter(regNameResult.name)
          }
        })
        .catch(error => console.log('*** ERROR *** Signin problem: ' + error.message))
    }
  }, [props, submittedName])

  return (
    <Stack direction='column' justifyContent='flex-start' component='form' onSubmit={submitName}
           sx={{backgroundColor: '#eee', padding: '.5rem', borderStyle: 'solid', borderRadius: '8px',
                fontFamily: 'Roboto', width: '30rem'}}>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <div>
          <b>Register:</b>
        </div>
        <Button onClick={() => props.stateSetter('signinattempt')} sx={{paddingTop: '0px'}}>
          <b>Sign In</b>
        </Button>
      </Stack>
      <Tooltip title='Enter the name you would like to be known by' placement='top'>
        <TextField id='name' name='name' label='Name' variant='outlined' fullwidth='true' autoFocus={true}
                   error={nameError}
                   helperText={nameHelperText}
                   onChange={(event) => onChangeCheckNameField(event.currentTarget, nameError)}
                   sx={{margin: '.5rem',
                        '& .MuiOutlinedInput-root': {borderRadius: '8px', backgroundColor: 'white',
                                                     '& fieldset': {borderWidth: '2px', borderColor: 'rgba(0, 0, 0, .07)'}}}}/>
      </Tooltip>
      <Box sx={{textAlign: 'center'}}>
        <Button variant='contained' type='submit'>
          Continue
        </Button>
      </Box>
    </Stack>
  )
}

const RegisterEmailBox = (props) => {
  const [emailError, setEmailError] = useState(false)
  const [emailHelperText, setEmailHelperText] = useState(null)
  const [submittedEmail, setSubmittedEmail] = useState('')

  const onChangeCheckEmailField = (target) => {
    if (emailError) {
      if (emailIsLegal(target.value)) {
        setEmailError(false)
        setEmailHelperText(null)
      }
    }
  }

  const submitEmail = (event) => {
    event.preventDefault()
    const Data = new FormData(event.currentTarget)
    const email = Data.get('email')

    if (emailIsLegal(email)) {
      // Legal email address
      setEmailError(false)
      setSubmittedEmail(email)
    } else {
      // Illegal email address, indicate error
      setEmailError(true)
      setEmailHelperText('Please enter a legal email address')
      return
    }
  }

  // myPost() (a layer atop fetch()) has to be employed inside useEffect so that the helper text
  // updates aren't a cycle late
  useEffect(() => {  
    if (submittedEmail) {
      const email = submittedEmail
      setSubmittedEmail('')
      myPost('/nt/regemail', {'email': email})
        .then(response => {
          if (!response.ok) {
            throw Error(response.statusText)
          }
          return response.json()
        })
        .then(regEmailResult => {
          if (regEmailResult.status === 'Error') {
            setEmailError(true)
            setEmailHelperText(regEmailResult.message)
          } else if (regEmailResult.status === 'Success') {
            props.stateSetter('regpassword')
            props.emailSetter(regEmailResult.email)
          }
        })
        .catch(error => console.log('*** ERROR *** Signin problem: ' + error.message))
    }
  }, [props, submittedEmail])

  return (
    <Stack direction='column' justifyContent='flex-start' component='form' onSubmit={submitEmail}
           sx={{backgroundColor: '#eee', padding: '.5rem', borderStyle: 'solid', borderRadius: '8px',
                fontFamily: 'Roboto', width: '30rem'}}>
      <Stack direction='row' justifyContent='space-between' alignItems='flex-start'>
        <Stack direction='column' alignItems='flex-start'>
          <Stack direction='row' justifyContent='left'>
            <ArrowBackIosIcon color='primary' onClick={() => props.stateSetter('regname')}
                           sx={{
                             margin: '-.2rem .2rem 0rem 0rem',
                             px: 0,
                             py: 0,
                             borderRadius: 1,
                             boxSizing: "content-box",
                             color: 'blue[500]',
                             cursor: 'pointer'
                           }}/>

            <b>Register:</b>
          </Stack>
          <Table className='SignInReg'>
            <TableBody>
              <TableRow>
                <TableCell align='right'>
                  Name:
                </TableCell>
                <TableCell align='left'>
                  {props.name}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Stack>
        <Button onClick={() => props.stateSetter('signinattempt')} sx={{paddingTop: '0px'}}>
          <b>Sign In</b>
        </Button>
      </Stack>
      <TextField id='email' name='email' label='Email Address' variant='outlined' fullwidth='true' autoFocus={true}
                 error={emailError}
                 helperText={emailHelperText}
                 onChange={(event) => onChangeCheckEmailField(event.currentTarget, emailError)}
                 sx={{margin: '.5rem',
                      '& .MuiOutlinedInput-root': {borderRadius: '8px', backgroundColor: 'white',
                                                   '& fieldset': {borderWidth: '2px', borderColor: 'rgba(0, 0, 0, .07)'}}}}/>
      <Box sx={{textAlign: 'center'}}>
        <Button variant='contained' type='submit'>
          Continue
        </Button>
      </Box>
    </Stack>
  )
}

const RegisterPasswordBox = (props) => {
  const [showPassword, setShowPassword] = useState(false)
  const [passwordError, setPasswordError] = useState(false)
  const [passwordHelperText, setPasswordHelperText] = useState(null)
  const [submittedPassword, setSubmittedPassword] = useState(null)

  const onChangeCheckPasswordField = (target) => {
    if (passwordError) {
      if (passwordIsLegal(target.value)) {
        setPasswordError(false)
        setPasswordHelperText(null)
      }
    }
  }

  const submitPassword = (event) => {
    event.preventDefault()
    const Data = new FormData(event.currentTarget)
    const password = Data.get('password')

    if (passwordIsLegal(password)) {
      // Legal password
      setPasswordError(false)
      setSubmittedPassword(password)
    } else {
      // Illegal password, indicate error
      setPasswordError(true)
      setPasswordHelperText('Please enter a password of at least 6 characters')
      return
    }
  }

  // If we've gotten this far then we previously were given a legal email not already in use by a
  // member, and now we've been given a legal password, so we can continue with the registration.
  // We'll POST the email and password to the backend which will attempt the registration and let us
  // know whether it succeeded or failed.
  useEffect(() => {
    if (submittedPassword) {
      const password = submittedPassword
      setSubmittedPassword('')
      myPost('/nt/regpassword', {'name': props.regName, 'email': props.email, 'password': password})
        .then(response => {
          if (!response.ok) {
            throw Error(response.statusText)
          }
          return response.json()
        })
        .then(regPasswordResult => {
          console.log('regPasswordResult: ' + JSON.stringify(regPasswordResult))
          if (regPasswordResult.status === 'Error') {
            // I can't think of a reason for an error since the email has already been validated and the
            // password is the correct length, but provide for it anyway in case some reason for error
            // comes up.
            setPasswordError(true)
            setPasswordHelperText('Registration rejected: ' + regPasswordResult.message)
          } else if (regPasswordResult.status === 'Success') {
            setSigninCookies(regPasswordResult.data.idnum, regPasswordResult.data.pword)
            props.credentialSetter(regPasswordResult.data)
            props.nameSetter(props.regName)
            props.stateSetter('regcomplete')
          }
        })
        .catch(error => console.log('*** ERROR *** Signin problem: ' + error.message))
    }
  }, [props, submittedPassword])

  return (
    <Stack direction='column' justifyContent='flex-start' component='form' onSubmit={submitPassword}
           sx={{backgroundColor: '#eee', padding: '.5rem', borderStyle: 'solid', borderRadius: '8px',
                fontFamily: 'Roboto', width: '30rem'}}>
      <Stack direction='row' justifyContent='space-between' alignItems='flex-start'>
        <Stack direction='column' alignItems='flex-start'>
          <Stack direction='row' justifyContent='left'>
            <ArrowBackIosIcon color='primary' onClick={() => props.stateSetter('regemail')}
                           sx={{
                             margin: '-.2rem .2rem 0rem 0rem',
                             px: 0,
                             py: 0,
                             borderRadius: 1,
                             boxSizing: "content-box",
                             color: 'blue[500]',
                             cursor: 'pointer'
                           }}/>

            <b>Register:</b>
          </Stack>
          <Table className='SignInReg'>
            <TableBody>
              <TableRow>
                <TableCell>
                  Name:
                </TableCell>
                <TableCell >
                  {props.regName}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Email:
                </TableCell>
                <TableCell>
                  {props.email}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Stack>
        <Button onClick={() => props.stateSetter('signinattempt')} sx={{paddingTop: '0px'}}>
          <b>Sign In</b>
        </Button>
      </Stack>
      <TextField id='password' name='password' label='Password' variant='outlined' fullwidth='true' type={showPassword ? 'text' : 'password'}
                 InputProps={{endAdornment: <InputAdornment position='end'>
                                              <IconButton
                                                aria-label='toggle password visibility'
                                                onClick={() => setShowPassword(!showPassword)}
                                                onMouseDown={(event) => event.preventDefault()}
                                                edge='end'
                                              >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                              </IconButton>
                                            </InputAdornment>}}
                 autoFocus={true} error={passwordError}
                 helperText={passwordHelperText}
                 onChange={(event) => onChangeCheckPasswordField(event.currentTarget)}
                 sx={{margin: '.5rem',
                      '& .MuiOutlinedInput-root': {borderRadius: '8px', backgroundColor: 'white',
                                                   '& fieldset': {borderWidth: '2px', borderColor: 'rgba(0, 0, 0, .07)'}}}}/>
      <Box sx={{textAlign: 'center'}}>
        <Button variant='contained' type='submit'>
          Submit
        </Button>
      </Box>
    </Stack>
  )
}

const RegisterCompleteBox = (props) => {
  return (
    <Stack direction='column' justifyContent='flex-start'
           sx={{backgroundColor: '#eee', padding: '.5rem', borderStyle: 'solid', borderRadius: '8px',
                fontFamily: 'Roboto', fontSize: '14pt', width: '30rem'}}>
      <Box>
        <p>
          <b>{props.name}</b> registration successful.
        </p>
        <p>
          You are signed in.  An email has been sent containing a validation link.  Click on that
          link to complete the registration process, which will permit you to post comments.
        </p>
      </Box>
      <Box sx={{textAlign: 'center', paddingTop: '1rem'}}>
        <Button variant='contained'
                onClick={() => {
                  props.popoverAnchorElSetter(null)
                  setTimeout(() => props.stateSetter('signedin'), 200)}}>
          Close
        </Button>
      </Box>
    </Stack>
  )
}

// ```` I want to clean up the signin and registration stuff.  Did most of it, one thing remaining:
// 1. When not signed in, when you click on the person icon the signin box comes up.  If you want to
//    register, that's a link.  But every time I register an account it feels like the registration
//    link is too inoccuous.  I want to somehow make that link more obvious.  How to do that?
//    Possibly make if a parenthesized comment: "Sign In (Register instead)", where Register is a
//    link.

// Appears at the top of every page
export const WebsiteHeader = (props) => {

  // Sign-in/Register stuff
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null)
  const displayPopover = (event) => {
    setPopoverAnchorEl(event.currentTarget)
  }
  const closeSigninPopover = () => {
    setPopoverAnchorEl(null)
    let signedin = false
    if (props.signinCredentials) {
      if (props.signinCredentials.idnum > 0) {
        setPopoverState('signedin')
        signedin = true
      }
    }
    if (!signedin) {
      setPopoverState('signinattempt')
    }
  }
  const open = useMemo(() => {
    return Boolean(popoverAnchorEl)
  }, [popoverAnchorEl])
  const id = open ? 'loginPopoverID' : undefined

  const [popoverState, setPopoverState] = useState('')
  const [name, setName] = useState('')
  const [regName, setRegName] = useState('')
  const [email, setEmail] = useState('')

  const {signinCredentials, credentialSetter, nosignin} = props
  const mbrID = signinCredentials ? signinCredentials.idnum : 0
  const primaryName = signinCredentials ? signinCredentials.pname : ''

  // Maintains users sign-in state.  Keeps the cookies and props.signinCredentials consistent.
  useEffect(() => {
    const mbridCookie = Number(getCookie('mbrid'))
    const password = getCookie('qx')
    if (!nosignin) {
      if (!mbrID) {
        // The signin credentials are not set, which is always the case when a page first loads, so
        // fetch the cookies and see if they are for a valid member account
        if (mbridCookie && password) {
          // We have cookie values for the member ID and password.  Check if they're for a valid
          // member account.
          myPost('/nt/checkSigninCookies', {mbrid: mbridCookie, qx: password})
            .then((res) => res.json())
            .then((retVal) => {
              if (retVal.status && retVal.status === 'Success') {
                // The mbridCookie and password were for a valid account, so sign the member in by setting
                // the signin credentials.  Also, set the cookies again so that they're valid for
                // another year.
                credentialSetter(retVal.data)
                setSigninCookies(mbridCookie, password)
              } else {
                // Cookie sign in failed so delete the cookies
                deleteSigninCookies()
              }
            })
            .catch((err) => {
              // Something went wrong, report it to the console and delete the cookies
              deleteSigninCookies()
            })
        } else {
          // If we're signed in, as indicated by a non-zero member ID, then if one of the signin
          // cookies was missing or had no value then sign us out and delete the cookies just to
          // make sure there's no member info hanging around
          if (mbrID) {
            credentialSetter({idnum: 0})
            deleteSigninCookies()
          }
        }
      } else {
        // The signin credentials are set.  Everything's okay unless the member ID's don't match
        // (which would be weird and would imply an attempted hack), in which case we sign the user
        // out
        if (mbrID !== mbridCookie) {
          credentialSetter({idnum: 0})
          deleteSigninCookies()
        }
      }
    } else {
      // Don't sign in - we're probably updating account settings
    }
  }, [mbrID, nosignin, signinCredentials, credentialSetter])

  useEffect(() => {
    let signedin = false
    if (mbrID > 0) {
      // We're signed in.  Unless we're in the process of notifying the user of a successful sign in
      // or of a successful registration set the popoverState to 'signedin'.
      if (popoverState !== 'regcomplete') {
        setPopoverState('signedin')
      }
      setName(primaryName)
      signedin = true
    }
    if (!signedin) {
      // Since we're not signed in, leave the popoverState alone unless it doesn't have a value
      if (!popoverState) {
        setPopoverState('signinattempt')
      }
      setName('')
    }

  }, [popoverState, primaryName, mbrID, signinCredentials])

  const popoverBox = useMemo(() => {
    switch (popoverState) {
    default:
    case 'regname':
      return <RegisterNameBox stateSetter={setPopoverState}
                              nameSetter={setRegName}/>
    case 'regemail':
      return <RegisterEmailBox stateSetter={setPopoverState}
                               name={regName}
                               emailSetter={setEmail}/>
    case 'regpassword':
      return <RegisterPasswordBox stateSetter={setPopoverState}
                                  credentialSetter={props.credentialSetter}
                                  regName={regName}
                                  nameSetter={setName}
                                  email={email}/>
    case 'regcomplete':
      setRegName('')
      return <RegisterCompleteBox stateSetter={setPopoverState}
                                  name={name}
                                  popoverAnchorElSetter={setPopoverAnchorEl}/>
    case 'signinattempt':
      return <SigninAttemptBox stateSetter={setPopoverState}
                               credentialSetter={props.credentialSetter}
                               popoverAnchorElSetter={setPopoverAnchorEl}/>
    case 'signedin':
      return <SignedinBox nameSetter={setName}
                          stateSetter={setPopoverState}
                          credentialSetter={props.credentialSetter}
                          popoverAnchorElSetter={setPopoverAnchorEl}/>
    }
  }, [email, name, regName, props.credentialSetter, popoverState])

  return (
    <Grid container sx={{backgroundColor: 'black', marginBottom: '10px', paddingRight: '.5rem'}}>
      <Grid sx={{marginLeft: 'auto', marginRight: 'auto'}}>
        <Typography align='center' display='flex' alignItems='center' fontSize='2.0rem' fontWeight='bold' color='white' sx={{height: '100%'}}>
            Discuss the<br/>Latest News and Articles
        </Typography>
      </Grid>
      <Grid sx={{marginLeft: 'auto', marginRight: 'auto'}}>
        <NewsTalkLogoLink />
      </Grid>
      <Grid sx={{marginLeft: 'auto'}}>
        <Stack direction='column' aria-describedby={id} onClick={(event) => displayPopover(event)} sx={{cursor: 'pointer'}}>
          {name
           ? <Stack direction='column' sx={{marginTop: '.4rem'}}>
               <Box sx={{marginLeft: 'auto', marginRight: 'auto'}}>
                 <AvatarDisplay {...props.signinCredentials}/>
               </Box>
               <Box fontSize='.8rem' fontWeight='bold' color='white' fontFamily='Roboto' sx={{margin: '.2rem auto 0rem auto'}}>
                 {name}
               </Box>
             </Stack>
           : <PersonIcon color='primary'
                         sx={{
                           margin: '.2rem auto -.4rem auto',
                           px: 1,
                           py: 1,
                           borderRadius: 1,
                           boxSizing: "content-box",
                         color: 'white'
                         }}/>
          }
        </Stack>
        <Popover id={id} open={Boolean(popoverAnchorEl)} sx={{'& .css-3bmhjh-MuiPaper-root-MuiPopover-paper': {borderRadius: '8px'}}}
                 anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                 transformOrigin={{vertical: 'top', horizontal: 'right'}}
                 anchorEl={popoverAnchorEl} onClose={closeSigninPopover}>
          {popoverBox}
        </Popover>
      </Grid>
    </Grid>
  )
}

// Appears at the top of every page specific to an Article's thread, such as the thread itself or a
// comment being composed to post to the thread
export const ArticleHeader = (props) => {
  const theme = createTheme({
    typography: {
      fontSize:   30,
      fontWeight: 500
    }
  })
  return (
    <Box sx={{...theme.typography, marginTop: '5px', marginBottom: '5px'}}>
      <Stack direction='row'>
        <MediaOutletImageLink MediaOutlet={props.Article.MediaOutlet}/>
        <Box align='center' sx={{width: '100%'}}>
          <Table>
            <TableBody>
              <TableListItem label='Section List'
                             value={<Link href={'/MediaOutlet/' + props.Article.MediaOutlet.MOID}>
                                      {props.Article.MediaOutlet.Name}
                                    </Link>}/>
              <TableListItem label='Section'
                             value={<Link href={'/Section/' + props.Article.Section.SID}>
                                      {props.Article.Section.Name}
                                    </Link>}/>
            </TableBody>
          </Table>
        </Box>
      </Stack>
      <Box align='center' sx={{margin: '30px', paddingLeft: '30px', paddingRight: '30px'}}>
        <Tooltip title='Return to page one of the discussion' placement='top'>
          <span>
            Article:{' '}
            <Link href={'/Article/AID/' + props.Article.MediaOutlet.MOID + '/' + props.Article.AID + '/1'}>
              {props.Article.Name}
            </Link>
          </span>
        </Tooltip>{' '}
        <Tooltip title={'Link to the article at the ' + props.Article.MediaOutlet.Name} placement='top'>
          <IconButton href={props.Article.URL} target='_blank'>
            <ArticleIcon color='primary'/>
          </IconButton>
        </Tooltip>
        <br/>
        <Box sx={{width: '100%', textAlign: 'center', fontSize: '12pt', margin: '0px', padding: '0px'}}>
          <FormattedDateTime TheDate={new Date(props.Article.Time)} />
        </Box>
      </Box>
    </Box>
  )
}

export const Copyright = (props) => {
  const TheDate = new Date()
  const year = TheDate.getFullYear()
  const yearRange = year > ConfigVars.OriginYear
        ? `${ConfigVars.OriginYear}-${year}`
        : year
  return (
    <Box>
      <Typography align='center' fontSize='.8rem' marginTop='2rem' color='grey.500'>
        Copyright {yearRange} by {props.siteData ? props.siteData.SiteName : ConfigVars.DefaultSiteName}, All Rights Reserved
        <br/>
        Innovative Software from {ConfigVars.CompanyName} &copy; {year}
      </Typography>
    </Box>
  )
}

const NewsTalkLogoLink = (props) => {
  let width = '300px'
  if (props.width) {
    width = props.width
  }
  return (
    <Link href='/'>
      <img src={nodeJsURL + '/nt/image/' + ConfigVars.Logo} width={width} alt='NewsTalk Logo' />
    </Link>
  )
}

export const MediaOutletImageLink = (props) => {
  return (
    <Paper elevation={7} sx={[props.margin, props.padding]}>
      <Link href={props.MediaOutlet.URL} target='_blank'>
        <img src={nodeJsURL + '/nt/image/' + props.MediaOutlet.ImageName} width='400px' alt='Media Logo' />
      </Link>
    </Paper>
  )
}

// For creating a table of items with a label on the left and a value on the right
export const LabeledList = (props) => {
  const defaultTheme = createTheme({
    typography: {
      fontSize: 16,
      body2: {
        fontWeight: 100
      }
    }
  })
  const theme = props.theme || defaultTheme
  let count=1;
  return (
    <ThemeProvider theme={theme}>
      <Table padding='none'>
        <TableBody key={'x' + count++}>
          {props.Rows.map((Datum) => {
            return (
              <TableRow key={'x' + count++}>
                <TableCell align='right' sx={{borderStyle: 'none', paddingRight: '10px', width: '10%',
                                              verticalAlign: 'top', whiteSpace: 'nowrap'}}>
                  {Datum.label}:&nbsp;
                </TableCell>
                <TableCell align='left' sx={{borderStyle: 'none', verticalAlign: 'top',
                                             paddingLeft: '15px', textIndent: '-15px'}}>
                  {Datum.value}
                </TableCell>
              </TableRow>
            )})}
        </TableBody>
      </Table>
    </ThemeProvider>
  )
}

export const MemberItem = (props) => {
  let name, avatar
  if (!props || !props.Member || !props.Member.pname) {
    name = 'No Name'
    avatar = <Avatar sx={{ width:50, height: 60, fontSize: 30 }}>X</Avatar>
  } else {
    name = props.Member.pname
    avatar = (<AvatarDisplay {...props.Member} placement='bottom-end'/>)
  }
  return (
    <Grid item>
      {name}
      {avatar}
    </Grid>
  )
}

const repliesTheme = createTheme({
  typography: {
    fontSize:   13,
    body2: {
      fontWeight: 100
    }
  },
})

// When props.apiRef is defined then the comment item is one of the comments in the display of an
// article thread inside a datagrid.  When it's not defined then its the comment being replied to
// while a reply is being composed.
export const CommentItem = (props) => {
  const messageText = <div className='messageDisplay' dangerouslySetInnerHTML={{__html: props.Comment.MessageText}} />
  const [editorRefReplyToComment, setEditorRefReplyToComment] = useState(null)
  useEffect(() => {
    if (editorRefReplyToComment) {
      editorRefReplyToComment.mode.set('readonly')
    }
  }, [editorRefReplyToComment])
  let subTopic = ''
  if (props.Comment.Title) {
    subTopic = <Grid item>
                 <Typography variant='subtitle1' sx={{paddingLeft: '10px', paddingRight: '10px', textAlign: 'center'}}>
                   <Typography sx={{textDecoration: 'underline'}}>
                     Subtopic<br/>
                   </Typography>
                   {props.Comment.Title}
                 </Typography>
               </Grid>
  }

  const RepliesInfo = []
  if (props.Comment.ReplyToMID) {
    RepliesInfo.push({label: 'This comment is a reply to',
                      value: props.apiRef
                      ? <CommentLink Comment={props.Comments[props.Comment.ReplyToMID]} apiRef={props.apiRef}/>
                      : <OffGridCommentLink Comment={props.Comments[props.Comment.ReplyToMID]} Section={props.Section}/>
                     })
  }
  if (props.Comment.ReplyMIDs) {
    let count = 1
    for(const MID of Object.keys(props.Comment.ReplyMIDs)) {
      RepliesInfo.push({label: 'Reply ' + count++ + ' to this comment',
                        value: props.apiRef
                        ? <CommentLink Comment={props.Comments[MID]} apiRef={props.apiRef}/>
                        : <OffGridCommentLink Comment={props.Comments[MID]} Section={props.Section}/>
                       })
    }
  }

  const repliesInfo = RepliesInfo.length
        ? <LabeledList Rows={RepliesInfo} theme={repliesTheme}/>
        : null

  const fetchMargin = () => {
    return props.apiRef
      ? '0px'
      : '5px 5px 0px 5px'
  }

  return (
    <>
      <Stack className='commentDisplay' id={'m' + props.Comment.MID} direction='column' sx={{width: '100%'}}>
        <Stack direction='row' justifyContent='space-between' alignItems='stretch' spacing={3}
               sx={{margin: fetchMargin()}}>
          <MemberItem Member={props.Comment.Member}/>
          <Grid container sx={{bottom: '0px', justifyContent: 'center'}}>
            {subTopic}
          </Grid>
          <Stack direction='column' justifyContent='left'>
            <Grid item sx={{whiteSpace: 'nowrap'}}>
              Comment {props.Comment.MsgNum} of {props.TotalMsgs}
            </Grid>
            <Grid item sx={{whiteSpace: 'nowrap'}}>
              <FormattedDateTime TheDate={new Date(props.Comment.Time)}/>
            </Grid>
          </Stack>
        </Stack>
        <Box>
          {props.apiRef
           ? messageText
           : <Editor
               tinymceScriptSrc={process.env.PUBLIC_URL + '/tinymce/tinymce.min.js'}
               onInit={(evt, editor) => {setEditorRefReplyToComment(editor)}}
               initialValue={props.Comment ? props.Comment.MessageText : null}
               justifyContents='center'
               init={{
                 promotion: false,
                 branding: false,
                 menubar: false,
                 toolbar: false,
                 statusbar: false,
                 height: 0,
                 min_height: 0,
                 autoresize_bottom_margin: 0,
                 width: '100%',
                 horizontalAlign: 'center',
                 paste_as_text: true,
                 plugins: [
                   'advlist', 'autolink', 'autoresize', 'lists', 'link', 'image', 'charmap',
                   'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                   'insertdatetime', 'media', 'table', 'preview', 'help', 'wordcount'
                 ],
                 content_style_skip: 'body { font-family:Roboto,Helvetica,Arial,sans-serif; font-size:19px; }',
                 content_css: '/TinyMCE.css',
                 save_onsavecallback: () => {
                 }
               }}
             />
          }
          <hr/>
          <Grid container direction='row' justifyContent='space-between' alignItems='flex-start' spacing={2} sx={{width: '100%'}}>
            <Grid item xs={9}>
              {repliesInfo}
            </Grid>
            {props.apiRef
             ? <Grid item xs={3} sx={{textAlign: 'right', marginBottom: '5px'}}>
                 {props.PostingAllowed
                  ? <Button variant='contained' size='small'
                            href={'/CommentReply/' + props.Comment.SID + '/' + props.Comment.AID + '/' + props.Comment.MID}>
                      Reply to This Comment
                    </Button>
                  : null
                 }
               </Grid>
             : null
            }
          </Grid>
        </Box>
      </Stack>
    </>
  )
}

// For producing a link to a specific comment inside a datagrid from within the same datagrid
export const CommentLink = (props) => {
  const Comment = props.Comment
  const text = props.includeTitle && Comment.Title
        ? Comment.Title + ' (Comment ' + Comment.MsgNum + ')'
        : 'Comment ' + Comment.MsgNum
  return (
    <>
      <Link onClick={() => gotoComment(Comment.MID, props.apiRef)}>
        {text}
      </Link>
      {' by ' + Comment.Member.pname}
      {' on '}
      <FormattedDateTime TheDate={new Date(Comment.Time)}/>
    </>
  )
}

// For producing a link to a specific comment inside a datagrid from outside that datagrid 
export const OffGridCommentLink = (props) => {
  const linkText = props.Comment.Title
        ? props.Comment.Title.replace(/(<([^>]+)>)/ig, '') + ' (Comment ' + props.Comment.MsgNum + ')'
        : 'Comment ' + props.Comment.MsgNum
  return (
    <>
      <Link href={'/Article/MID/' + props.Section.SID + '/' + props.Comment.MID}>{linkText}</Link>
    </>
  )
}

// ```` Make the below stuff go away and use LabeledList instead

const TC = styled(TableCell)({
  borderStyle:  'none',
  margin:       0,
  fontSize:     30,
  fontWeight:   500
})

const TCLabel = styled(TC)({
  padding:      '0px 8px 0px 0px',
  textAlign:    'right'
})

const TCValue = styled(TC)({
  padding:      0,
})

export const TableListItem = (props) => {
  return (
    <TableRow>
      <TCLabel>
        {props.label}:
      </TCLabel>
      <TCValue>
        {props.value}
      </TCValue>
    </TableRow>
  )
}

export const PageListPagination = () => {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  
  return (
    <Pagination
      color='primary'
      count={pageCount}
      siblingCount={3}
      page={page+1}
      shape='rounded'
      sx={{button:{'&:hover':{backgroundColor: '#ddd'}}}}
      onChange={(event, value) => apiRef.current.setPage(value - 1)}
    />
  )
}

export const PageListPaginationRightAlign = () => {
  return (
    <Box sx={{width: '100%', display: 'flex', justifyContent: 'flex-end', alignItems: 'center'}}>
      <PageListPagination/>
    </Box>
  )
}

export const ReplyButtonAndPagination = (props) =>{
  const buttonText = props.PostCount > 0 
    ? 'Reply to Thread'
    : 'Comment on Article'
  return (
    <Stack direction='row' sx={{width: '100%'}}>
      {props.PostingAllowed
       ? <Button variant='contained' href={'/CommentReply/' + props.SID + '/' + props.AID} sx={{whiteSpace: 'nowrap', marginLeft: '10px'}}>
           {buttonText}
         </Button>
       : null
      }
      {props.PostCount > ConfigVars.MessagesPerPage
       ? <PageListPaginationRightAlign/>
       : null
      }
    </Stack>
  )
}

export const ReplyButtonAndPaginationToolbar = (props) => {
  return (
    <Box sx={{width: '100%', borderBottom: 1, borderColor: 'grey.300', marginTop: .9, paddingBottom: .9,
              display: 'flex', justifyContent: 'flex-end'}}>
      <ReplyButtonAndPagination {...props}/>
    </Box>
  )
}

export const ReplyButtonAndPaginationFooter = (props) => {
  return (
    <Box sx={{width: '100%', marginTop: .9, marginBottom: .9,
              display: 'flex', justifyContent: 'flex-end'}}>
      <ReplyButtonAndPagination {...props}/>
    </Box>
  )
}

const theme = createTheme({
  palette: {
    pagination: {
      main: 'transparent',
      contrastText: 'black'
    }
  }
})

export const ArticleItem = (Article) => {
  if (Article) {
    const numPages = parseInt(Math.ceil(Article.PostCount / ConfigVars.MessagesPerPage));
      const pagination = numPages > 0
          ? <ThemeProvider theme={theme}>
              <Pagination
                color='pagination'
                count={numPages}
                hideNextButton={true}
                hidePrevButton={true}
                page={numPages}
                siblingCount={0}
                shape='rounded'
                size='small'
                sx={{button:{'&:hover':{backgroundColor: '#ddd'}}}}
                onChange={(event, value) =>
                  {window.location='/Article/AID/' + Article.Section.SID + '/' + Article.AID + '/' + value}}
              />
            </ThemeProvider>
          : <span>{Article.noCommentsYet}</span>
    return (
      <Stack sx={{width: '100%'}}>
        <Stack direction='row' justifyContent='space-between'>
          <Grid item align='left' sx={{marginTop: '5px'}}>
            <Tooltip title={Article.threadToolTip} placement='top'>
              <Link href={'/Article/AID/' + Article.Section.SID + '/' + Article.AID + '/1'} marginTop={1} fontSize='1.0rem'>
                {Article.Name}
              </Link>
            </Tooltip>
          </Grid>
          <Grid item align='right'>
            <Tooltip title={Article.articleToolTip} placement='top'>
              <IconButton href={Article.URL} target='_blank'>
                <ArticleIcon color='primary'/>
              </IconButton>
            </Tooltip>
          </Grid>
        </Stack>
        <Stack direction='row' justifyContent='space-between' sx={{width: '100%', whiteSpace: 'nowrap',
                                                                   marginBottom: '1rem', marginTop: '.5rem'}}>
          <FormattedDateTime TheDate={new Date(Article.Time)} />
          {pagination}
        </Stack>
      </Stack>
    )
  } else {
    return (
      <>
      </>
    )
  }
}

const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    padding: 0,
    maxWidth: 700,
    borderStyle: 'none',
    backgroundColor: 'transparent'
  },
});

export const AvatarDisplay = (props) => {
  if (!props.Avatar || !props.Avatar.FileName || props.Avatar.FileName === 'default') {
    if (props.onlyIfExists) {
      return null
    }
    // Use member's initial for the avatar
    return (
      <Avatar sx={{ width: props.width ? props.width : ConfigVars.Avatars_DisplayWidth,
                    height: props.height ? props.height : ConfigVars.Avatars_DisplayHeight,
                    fontSize: props.fontSize ? props.fontSize : ConfigVars.Avatars_FontSize }}>
        {props.pname.substr(0,1)}
      </Avatar>
    )
  }
  // Calculate width of image tooltip
  const maxAvatarWidth = parseInt(window.innerWidth / 2)
  const avatarWidth = props.Avatar.Width > maxAvatarWidth
        ? maxAvatarWidth
        : props.Avatar.Width
  const placement = props.placement
        ? props.placement
        : 'top'
  const src = `${nodeJsURL}/nt/image/Avatars/${props.Avatar.FileName}`
  return (
    <CustomWidthTooltip placement={placement}
                        title={<img src={src} alt='test' width={avatarWidth}
                                    style={{borderStyle: 'solid', borderWidth: '2px', borderColor: 'black', padding: '0px'}}/>}>
      <Avatar src={src} alt='test'
              sx={{ width: props.width ? props.width : ConfigVars.Avatars_DisplayWidth,
                    height: props.height ? props.height : ConfigVars.Avatars_DisplayHeight }}/>
    </CustomWidthTooltip>
  )
}

export const MainPageButton = (props) => {
  return (
    <Button variant='contained' onClick={() => window.location = '/'} sx={{marginLeft: 'auto', marginRight: 'auto'}}>
      Go to Main Page
    </Button>
  )
}

export const MessageBox = (props) => {
  const [color, borderColor] = props.color
        ? [props.color, props.color]
        : ['inherit', 'inherit']
  const borderStyle = props.borderStyle
        ? props.borderStyle
        : 'solid'
  const stackSx = {width: '80%', margin: 'auto', color: color, borderStyle: borderStyle, borderColor: borderColor}
  return (
      <Stack justifyContent='center' fontFamily='Roboto' alignItems='center' fontSize='1.2rem' sx={stackSx}>
        <div style={{padding: '2rem 3rem'}}>
          {props.children}
        </div>
      </Stack>

   )
}

// Format the time.  Arguments:
//
//  TheDate      A Date Object
//  timeFormat   12 or 24
export const FormattedTime = (props) => {
  let hour = props.TheDate.getHours()
  const min = props.TheDate.getMinutes()
  let time
  if (props.timeFormat !== '24') {
    let ampm
    if (hour < 12) {
      ampm = 'AM'
      if (hour === 0) {
        hour = 12
      }
    } else {
      ampm = 'PM'
      if (hour > 12) {
        hour -= 12
      }
    }
    time = hour + ':' + padZeros(min) + ' ' + ampm
  } else {
    time = hour + ':' + padZeros(min)
  }
  return (
    <>
      {time}
    </>
  )
}

// Interestingly, neither React nor MUI has a component for displaying a date.  This is stolen from
// dBoard.  Arguments are:
// 
//   TheDate       Date object
//   dateFormat    A bnuch of date formats that I chose for dBoard (default 'US')
//   dateSep       Between the month, day and year (default '-')
//   digitsYear    Number of digits in the year (default 4)
export const FormattedDate = (props) => {
  const dateFormat = props.dateFormat ? props.dateFormat : 'US'
  const dateSep = props.dateSep ? props.dateSep : '-'
  const digitsYear = props.digitsYear ? props.digitsYear : 4
  const paddedDay = props.paddedDay ? props.paddedDay : true
  const paddedMonth = props.paddedMonth ? props.paddedMonth : false
  return (
    <>
      {FormatDate(props.TheDate, dateFormat, dateSep, digitsYear, paddedDay, paddedMonth)}
    </>
  )
}

// Format the date and time, one appearing right after the other.  Arguments:
//
//   TheDate       Date object
//   dateFormat    A bnuch of date formats that I chose for dBoard (default 'US')
//   dateSep       Between the month, day and year (default '-')
//   digitsYear    Number of digits in the year (default 4)
export const FormattedDateTime = (props) => {
  return (
    <>
      <FormattedDate TheDate={props.TheDate} dateFormat={props.dateFormat} dateSep={props.dateSep} yearDig={props.yearDig} />
      &nbsp;
      <FormattedTime TheDate={props.TheDate} timeFormat={props.timeFormat} />
    </>
  )
}
